import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest } from 'rxjs';
import * as pbi from 'powerbi-client';
import { NavigationService } from 'src/app/services/navigation.service';
import { PowerBiService } from '../services/powerbi.service';
import { PowerBiEmbedResponse } from '../models/powerbi-embed.model';
import { POWER_BI_REPORT_LABEL } from '../powerbi.constants';
import { getPowerBiFolderForModule } from '../powerbi-module-folder.config';

export type PowerBiBackMode = 'directGallery' | 'moduleDashboard';

@Component({
  selector: 'app-powerbi-report-viewer',
  templateUrl: './powerbi-report-viewer.component.html',
  styleUrls: ['./powerbi-report-viewer.component.scss'],
})
export class PowerBiReportViewerComponent implements OnInit, OnDestroy {
  @Input() showHeader = true;
  @Input() backMode: PowerBiBackMode = 'directGallery';
  @Output() reportNameChange = new EventEmitter<string>();

  @ViewChild('reportContainer', { static: true })
  private readonly reportContainer!: ElementRef<HTMLDivElement>;

  private readonly powerBiServiceInstance = new pbi.service.Service(
    pbi.factories.hpmFactory,
    pbi.factories.wpmpFactory,
    pbi.factories.routerFactory
  );

  private embeddedReport: pbi.Report | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  reportId: string | null = null;
  moduleName = '';
  reportName = POWER_BI_REPORT_LABEL;
  loadingReport = true;
  errorMessage: string | null = null;

  private clientKey: string | null = null;
  private moduleKey: string | null = null;

  constructor(
    private readonly powerBiService: PowerBiService,
    private readonly route: ActivatedRoute,
    private readonly navigationService: NavigationService
  ) {}

  ngOnInit(): void {
    combineLatest([this.route.params, this.route.queryParams]).subscribe(
      ([params, queryParams]) => {
        this.reportId = params['reportId'] ?? null;
        this.moduleName = params['module'] ?? '';

        const decrypted = this.navigationService.decryptData(queryParams);
        this.clientKey = decrypted['cl_key'];
        this.moduleKey = decrypted['md_key'];

        if (!this.reportId) {
          this.navigateToGallery();
          return;
        }

        this.loadReportContext(this.reportId);
      }
    );
  }

  ngOnDestroy(): void {
    this.clearRefreshTimer();
    this.resetEmbed();
  }

  backToGallery(): void {
    this.navigateToGallery();
  }

  retry(): void {
    if (this.reportId) {
      this.errorMessage = null;
      this.loadingReport = true;
      this.loadEmbedToken(this.reportId);
    }
  }

  private navigateToGallery(): void {
    const query: Record<string, string> = {};
    if (this.clientKey) {
      query['cl_key'] = this.clientKey;
    }
    if (this.moduleKey) {
      query['md_key'] = this.moduleKey;
    }

    if (this.backMode === 'moduleDashboard') {
      this.navigationService.routerNavigate(`dashboard/${this.moduleName}`, query);
      return;
    }

    this.navigationService.routerNavigate(`direct/${this.moduleName}`, query);
  }

  private loadReportContext(reportId: string): void {
    const folderName =
      this.backMode === 'moduleDashboard'
        ? this.moduleName
        : this.moduleKey
          ? getPowerBiFolderForModule(Number(this.moduleKey))
          : undefined;
    const reports$ = folderName
      ? this.powerBiService.getReportsByFolder(folderName)
      : this.powerBiService.getReports();

    reports$.subscribe({
      next: (reports) => {
        const report = reports.find((item) => item.id === reportId);
        this.reportName = report?.name ?? POWER_BI_REPORT_LABEL;
        this.reportNameChange.emit(this.reportName);
        this.loadEmbedToken(reportId);
      },
      error: () => {
        this.reportName = POWER_BI_REPORT_LABEL;
        this.reportNameChange.emit(this.reportName);
        this.loadEmbedToken(reportId);
      },
    });
  }

  private loadEmbedToken(reportId: string): void {
    this.errorMessage = null;
    this.loadingReport = true;
    this.clearRefreshTimer();
    this.resetEmbed();

    this.powerBiService.getEmbedToken(reportId).subscribe({
      next: (response) => this.embedReport(response),
      error: (error) => {
        this.loadingReport = false;
        this.errorMessage =
          error?.error?.message ??
          error?.message ??
          'Failed to load Power BI embed token from the API.';
      },
    });
  }

  private embedReport(response: PowerBiEmbedResponse): void {
    const container = this.reportContainer.nativeElement;

    this.powerBiServiceInstance.reset(container);

    const config: pbi.IEmbedConfiguration = {
      type: 'report',
      id: response.reportId,
      embedUrl: response.embedUrl,
      accessToken: response.embedToken,
      tokenType: pbi.models.TokenType.Embed,
      settings: {
        panes: {
          filters: { expanded: false, visible: true },
          pageNavigation: { visible: true },
        },
        background: pbi.models.BackgroundType.Transparent,
      },
    };

    this.embeddedReport = this.powerBiServiceInstance.embed(
      container,
      config
    ) as pbi.Report;

    this.embeddedReport.on('loaded', () => {
      this.loadingReport = false;
      this.scheduleTokenRefresh(response.reportId, response.expiry);
    });

    this.embeddedReport.on('error', (event: pbi.service.ICustomEvent<unknown>) => {
      this.loadingReport = false;
      const detail = event.detail as { message?: string } | undefined;
      this.errorMessage =
        detail?.message ?? 'An error occurred while rendering the report.';
    });
  }

  private scheduleTokenRefresh(reportId: string, expiry: string): void {
    this.clearRefreshTimer();

    const expiryTime = new Date(expiry).getTime();
    const refreshAt = expiryTime - 5 * 60 * 1000;
    const delay = refreshAt - Date.now();

    if (delay <= 0) {
      return;
    }

    this.refreshTimer = setTimeout(() => {
      this.powerBiService.getEmbedToken(reportId).subscribe({
        next: (response) => {
          if (this.embeddedReport) {
            this.embeddedReport.setAccessToken(response.embedToken);
            this.scheduleTokenRefresh(reportId, response.expiry);
          }
        },
        error: () => {
          this.errorMessage =
            'The embed token expired and could not be refreshed. Please reload the page.';
        },
      });
    }, delay);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private resetEmbed(): void {
    if (this.embeddedReport) {
      this.embeddedReport.off('loaded');
      this.embeddedReport.off('error');
      this.embeddedReport = null;
    }

    this.powerBiServiceInstance.reset(this.reportContainer.nativeElement);
  }
}
